import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { FileText, Loader2, Check, ArrowRight } from "lucide-react";
import { subscribeNewsletter } from "@/lib/newsletter.functions";
import { track } from "@/lib/analytics";

/** Category-tailored dossier names so the unlock never feels generic. */
const DOSSIERS: Record<string, { name: string; promise: string }> = {
  "wild-things": {
    name: "Predator Dossier",
    promise: "The field notes on the animals that shouldn't be able to do what they do.",
  },
  "deep-ocean": {
    name: "Abyss Dossier",
    promise: "What lives below 1,000 metres - and why almost none of it makes sense.",
  },
  "human-body": {
    name: "Body Dossier",
    promise: "The things happening inside you right now that you never agreed to.",
  },
  space: {
    name: "Cosmos Dossier",
    promise: "The scale problems your brain physically refuses to picture.",
  },
};

const DEFAULT_DOSSIER = {
  name: "Field Dossier",
  promise: "The discoveries too strange to fit in a single article.",
};

type Props = {
  categorySlug?: string | null;
  categoryLabel?: string | null;
};

/**
 * Contextual Drop - appears only after a reader certifies a discovery.
 * One email-gated, category-specific dossier + one real product card.
 * No generic grids, no passive waitlists.
 */
export function ContextualDrop({ categorySlug, categoryLabel }: Props) {
  const subscribe = useServerFn(subscribeNewsletter);
  const [email, setEmail] = useState("");
  const dossier = (categorySlug && DOSSIERS[categorySlug]) || DEFAULT_DOSSIER;

  const join = useMutation({
    mutationFn: () =>
      subscribe({
        data: { email: email.trim().toLowerCase(), ...(categorySlug ? { categorySlug } : {}) },
      }),
    onSuccess: () => track("Dossier Unlocked", { category: categorySlug ?? "none" }),
  });

  return (
    <div className="mt-5 grid gap-3">
      {/* Email-gated dossier */}
      <div className="rounded-2xl border border-border bg-card/60 p-4">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
          <FileText className="h-3.5 w-3.5" /> Unlocked drop
        </div>
        <p className="mt-2 font-display text-lg uppercase leading-tight">
          The {categoryLabel ? `${categoryLabel} ` : ""}
          {dossier.name}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{dossier.promise}</p>

        {join.isSuccess ? (
          <p className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary">
            <Check className="h-4 w-4" /> On its way - check your inbox.
          </p>
        ) : (
          <form
            className="mt-3 flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              if (email.includes("@")) join.mutate();
            }}
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="min-w-0 flex-1 rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary"
            />
            <button
              type="submit"
              disabled={join.isPending}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl border border-primary bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
            >
              {join.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Send it to me
            </button>
          </form>
        )}
        {join.isError && (
          <p className="mt-2 text-xs text-muted-foreground">That didn't go through. Try again in a moment.</p>
        )}
      </div>

      {/* Real product, one card, direct */}
      <Link
        to="/shop/ultimate-trivia-pack"
        onClick={() => track("Contextual Product Click", { category: categorySlug ?? "none" })}
        className="flex items-center gap-3 rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 to-transparent p-4 transition hover:border-primary"
      >
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">For certified minds</div>
          <div className="mt-1 font-display text-base uppercase leading-tight">
            The Ultimate Trivia Pack
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            100 questions that make people say “wait, WHAT?” - print it, host it, win the room.
          </p>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-primary" />
      </Link>
    </div>
  );
}
